import type { PlotMeasure } from '../types/compute';
import { ALGORITHM_OVERLAY } from './catalogOverlay';

/**
 * The measure vocabulary shared by the config UI (MeasureSelector), the summary
 * helpers and the dashboard mapper. Keeps labels + ordering in one place.
 */

/** Default plotted measures for a tonnage algorithm — mirrors the historical
 *  chart (tonnage + grade) so an unset selection renders identically. */
export const DEFAULT_PLOT_MEASURES: PlotMeasure[] = ['tonnage', 'grade'];

/** Canonical order the checkboxes render in and selections are normalized to. */
export const MEASURE_ORDER: PlotMeasure[] = ['tonnage', 'metal', 'grade'];

export const MEASURE_LABELS: Record<PlotMeasure, string> = {
  tonnage: 'Tonelaje',
  metal: 'Metal',
  grade: 'Ley',
};

/** Grade is always a percentage; the mass measures switch t↔Mt (see below). */
export const GRADE_UNIT = '%';

/**
 * Longer measure labels for axes and legends of the cut-off tonnage chart —
 * grade reads "Ley media" (mean grade above the cut-off), unlike the short
 * `MEASURE_LABELS` used in the chart title.
 */
export const MEASURE_SERIES_LABELS: Record<PlotMeasure, string> = {
  tonnage: 'Tonelaje',
  metal: 'Metal',
  grade: 'Ley media',
};

/** Mass unit for tonnage/metal: millions of tonnes when the algorithm's
 *  `return_m_tonnage` flag is on, plain tonnes otherwise. */
export function tonnageUnit(returnMTonnage: unknown): 't' | 'Mt' {
  return returnMTonnage === true ? 'Mt' : 't';
}

/** Display unit for a measure: `%` for grade, else the mass unit (t/Mt). */
export function measureUnit(m: PlotMeasure, returnMTonnage: unknown): string {
  return m === 'grade' ? GRADE_UNIT : tonnageUnit(returnMTonnage);
}

/** The algorithms whose charts let the user pick which measures to plot. */
export function isTonnageAlgorithm(name: string): boolean {
  return (
    name === 'tonnage_grade_metal_by_cutoff' || name === 'tonnage_grade_metal_by_categories'
  );
}

/** Re-order an arbitrary selection into the canonical measure order. */
export function normalizeMeasures(selection: PlotMeasure[]): PlotMeasure[] {
  return MEASURE_ORDER.filter((m) => selection.includes(m));
}

/**
 * The default figure name for a tonnage chart (SPEC/2240), built from which
 * measures are plotted rather than a fixed label — "Tonelaje - Ley" with just
 * tonnage+grade checked, "Metal - Ley" with metal+grade, etc. All three
 * checked keeps the algorithm's own established name (`ALGORITHM_OVERLAY`'s
 * "Tonelaje - Ley - Metal") rather than the tonnage/metal/grade order
 * `MEASURE_ORDER` would otherwise produce for that combination.
 */
export function tonnageFigureLabel(name: string, measures?: PlotMeasure[]): string {
  const selected = normalizeMeasures(measures && measures.length > 0 ? measures : DEFAULT_PLOT_MEASURES);
  if (selected.length >= 3) return ALGORITHM_OVERLAY[name]?.label ?? name;
  return selected.map((m) => MEASURE_LABELS[m]).join(' - ');
}
